import { Injectable } from '@angular/core';

import { environment } from '../../../environments/environment';
import {HttpClient, HttpHeaders} from '@angular/common/http';
import {JwtHelperService} from '@auth0/angular-jwt';
import {Customer} from '../models/customer.model';
import {LocalStorageService} from 'ngx-webstorage';
import {catchError, map} from 'rxjs/operators';
import {LoggerService} from './logger.service';
import {Observable, of, Subject, throwError} from 'rxjs';
import {TranslateService} from '@ngx-translate/core';
import {LoginResponse} from '../../login/state/user.reducers';
import {AllAuthLoginResponse} from '../models/allauth.model';

@Injectable({
  providedIn: 'root'
})
export class AllauthService {

  private loggedInSource = new Subject<void>();
  private loggedOutSource = new Subject<void>();

  loggedIn$ = this.loggedInSource.asObservable();
  loggedOut$ = this.loggedOutSource.asObservable();

  constructor(private http: HttpClient,
              private jwtHelper: JwtHelperService,
              private localStorage: LocalStorageService,
              private logger: LoggerService,
              private translate: TranslateService) { }

  public isTokenExpired(token: string): boolean {
    return this.jwtHelper.isTokenExpired(token);
  }

  public setSuperUserCustomer(customer: Customer): void {
    this.localStorage.store('customer', JSON.stringify(customer));
  }

  public login(email: string, password: string): Observable<AllAuthLoginResponse> {
    const url = environment.apiUrl.slice(0, -3) + '/_allauth/app/v1/auth/login';
    let headers = new HttpHeaders();
    headers = headers.append('Content-Type', 'application/json');
    const body = JSON.stringify({ username: email, password });

    return this.http.post<AllAuthLoginResponse>(url, body, { headers }).pipe(
      map(this.extractAndStoreToken, this),
      map((allAuthLoginResponse: AllAuthLoginResponse) => {
        setTimeout(() => this.loggedInSource.next(), 2000);
        return allAuthLoginResponse;
      }),
      catchError((allAuthLoginResponse: AllAuthLoginResponse) => {
        return throwError(allAuthLoginResponse);
      })
    );
  }

  logout(): Observable<any> {
    const url = environment.apiUrl.slice(0, -3) + '/_allauth/app/v1/auth/session';
    return this.http.delete(url).pipe(
      map((response: any) => {
        setTimeout(() => this.loggedOutSource.next(), 200);
        return response;
      }),
      catchError(this.handleError)
    );
  }

  mfaAuthentication(code: string): Observable<AllAuthLoginResponse> {
    const url = environment.apiUrl.slice(0, -3) + '/_allauth/app/v1/auth/2fa/authenticate';
    let headers = new HttpHeaders();
    headers = headers.append('Content-Type', 'application/json');
    const body = JSON.stringify({ code });
    return this.http.post<AllAuthLoginResponse>(url, body, { headers }).pipe(
      map((allAuthLoginResponse: AllAuthLoginResponse) => {
        setTimeout(() => this.loggedInSource.next(), 2000);
        return allAuthLoginResponse;
      }),
      catchError((allAuthLoginResponse: AllAuthLoginResponse) => {
        return throwError(allAuthLoginResponse);
      })
    );
  }

  getNewJwt(token: string): Observable<LoginResponse> {
    // Get a new JWT using the token saved in local storage.
    // The HttpErrorResponse is deliberately left to propagate: callers need the
    // status to tell a rejected token apart from a transient server problem.
    const url = environment.apiUrl.slice(0, -3) + '/api-token-refresh/';
    let headers = new HttpHeaders();
    headers = headers.append('Content-Type', 'application/json');
    const body = JSON.stringify({ token });
    return this.http.post<LoginResponse>(url, body, { headers });
  }

  private extractAndStoreToken(data: any): any {
    // set language
    this.translate.use(data.meta.user.locale);
    return data || {};
  }

  private handleError(error: any): Observable<never> {
    // In a real world app, we might use a remote logging infrastructure
    // We'd also dig deeper into the error to get a better message
    const errMsg = error.message
      ? error.message
      : error.status
        ? `${error.status} - ${error.statusText}`
        : 'Server error';
    console.error(errMsg); // log to console instead
    return throwError(errMsg);
  }
}
